import { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import Input from "../../components/Input";
import GlbViewer from "./GlbViewer";
import ModelComponentMappingMock from "./ModelComponentMappingMock";

const isValidCm = (v) => Number.isFinite(v) && v > 0;

const WidthValueModelModal = ({ isOpen, onClose, onSave, colorValues = [], colorElementMappings = {}, editingValue, existingModel }) => {
  const isEditing = editingValue != null;
  const [step, setStep] = useState(isEditing ? 2 : 1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Step 1: user enters cm (e.g. 90, 100, 110)
  const [cmInput, setCmInput] = useState(isEditing ? String(editingValue) : "");
  const cmValue = useMemo(() => {
    const n = Number(cmInput);
    return Number.isFinite(n) ? n : NaN;
  }, [cmInput]);

  // Step 2: file + preview
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [mappingReady, setMappingReady] = useState(false);
  const [mappingState, setMappingState] = useState(null);

  useEffect(() => {
    if (isOpen && isEditing) {
      setStep(2);
      setCmInput(String(editingValue));
      if (existingModel) {
        setFile(existingModel.file || null);
        setPreviewUrl(existingModel.previewUrl || "");
      }
    } else if (!isOpen) {
      setStep(1);
      setCmInput("");
      setFile(null);
      setIsProcessing(false);
      // Only revoke if it's not a cached URL from existingModel
      if (previewUrl && previewUrl !== existingModel?.previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl("");
      setMappingReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const canGoNext = isValidCm(cmValue);
  const canSave = isValidCm(cmValue) && !!file && !!previewUrl && mappingReady;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    const url = URL.createObjectURL(f);
    setFile(f);
    setPreviewUrl(url);
  };

  const handleSave = () => {
    if (isProcessing) return;

    setIsProcessing(true);

    setTimeout(() => {
      const storedValue = cmValue;

      onSave?.({
        storedValue, // cm
        cmValue,
        mmValue: cmValue * 10,
        file,
        previewUrl,
        componentMapping: mappingState?.mapping,
        selectedColors: mappingState?.selectedColors,
      });

      setIsProcessing(false);
      onClose?.();
    }, 15000);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div style={{ padding: "var(--gap)" }}>
        <div style={{ marginBottom: "var(--gap)" }}>
          <h2 style={{ margin: 0 }}>
            {step === 1 ? "Pridėti plotį" : "Įkelti 3D modelį"}
          </h2>
          <div style={{ height: "var(--width-4)" }} />
          <p style={{ margin: 0, opacity: 0.8 }}>
            {step === 1
              ? "1 žingsnis iš 2: įveskite plotį (cm)."
              : "2 žingsnis iš 2: įkelkite .glb/.gltf ir peržiūrėkite."}
          </p>
        </div>

        {step === 1 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--gap-sm)",
            }}
          >
            <Input
              label="Plotis (cm)"
              name="width_cm"
              type="number"
              min="1"
              step="1"
              value={cmInput}
              onChange={(e) => setCmInput(e.target.value)}
              invalid={cmInput !== "" && !canGoNext}
              disabled={isProcessing}
            />
            {!canGoNext && cmInput !== "" ? (
              <p style={{ margin: 0, color: "var(--clr-red-700)" }}>
                Įveskite teigiamą skaičių (cm).
              </p>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--gap-sm)",
              position: "relative",
            }}
          >
            <p style={{ margin: 0, opacity: 0.8 }}>
              Plotis: <b>{cmValue} cm</b>
            </p>

            <Input
              label="3D modelis"
              name="width_model"
              type="file"
              accept=".glb,.gltf,model/gltf-binary,model/gltf+json"
              onChange={handleFileChange}
              disabled={isProcessing}
            />

            <div style={{ marginTop: "var(--gap-sm)", position: "relative" }}>
              {previewUrl ? (
                <>
                  {/* Loading overlay for demo */}
                  {isProcessing ? (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255,255,255,0.8)",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "12px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <p style={{ margin: 0, fontWeight: 600 }}>
                          Įkeliama ir apdorojama…
                        </p>
                        <p style={{ margin: 0, opacity: 0.8 }}>
                          Tai gali užtrukti kelias sekundes
                        </p>
                      </div>
                    </div>
                  ) : null}

                  <GlbViewer glbUrl={previewUrl} />

                  <Input
                    type="checkbox"
                    name="use_preset_angle"
                    label="Paveikslėlius generuoti iš standartinio kampo"
                    value={true}
                    disabled
                  />

                  <ModelComponentMappingMock
                    colorValues={colorValues}
                    colorElementMappings={colorElementMappings}
                    onReadyChange={setMappingReady}
                    initialMapping={existingModel?.componentMapping}
                    initialSelectedColors={existingModel?.selectedColors}
                    onStateChange={setMappingState}
                  />
                </>
              ) : (
                <div
                  style={{
                    height: "520px",
                    borderRadius: "12px",
                    border: "1px dashed var(--clr-grey-300)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.8,
                  }}
                >
                  Įkelkite failą, kad pamatytumėte peržiūrą
                </div>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--gap-sm)",
            justifyContent: "flex-end",
            marginTop: "var(--gap)",
          }}
        >
          <Button
            onClick={() => {
              if (step === 1) onClose?.();
              else setStep(1);
            }}
            disabled={isProcessing}
          >
            {step === 1 ? "Atšaukti" : "Atgal"}
          </Button>

          {step === 1 ? (
            <Button
              important
              disabled={!canGoNext || isProcessing}
              onClick={() => setStep(2)}
            >
              Toliau
            </Button>
          ) : (
            <Button
              important
              disabled={!canSave || isProcessing}
              onClick={handleSave}
            >
              {isProcessing ? "Apdorojama..." : "Generuoti variacijas"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default WidthValueModelModal;
